import { styled } from "@mui/material/styles";
import MuiAvatar from "@mui/material/Avatar";
import type { AvatarProps as MuiAvatarProps } from "@mui/material/Avatar";
import type { Theme } from "@mui/material/styles";
import getColors from "utils/getColors";

// ==============================|| TYPES ||============================== //

type ColorType = "filled" | "outlined" | "combined" | "default";

type SizeType = "badge" | "xs" | "sm" | "md" | "lg" | "xl";

interface ColorStyleParams {
  theme: Theme;
  color: string;
  type: ColorType | undefined;
}

interface AvatarStyleProps {
  color: string;
  type: ColorType | undefined;
  size: SizeType;
}

interface ColorStyles {
  color: string;
  background: string;
  border?: string;
  borderColor?: string;
}

interface SizeStyles {
  fontSize: string;
  width: number;
  height: number;
  border?: string;
}

export interface AvatarProps extends Omit<MuiAvatarProps, "color"> {
  children?: React.ReactNode | string;
  color?: string;
  type?: ColorType;
  size?: SizeType;
}

// ==============================|| AVATAR - COLOR STYLE ||============================== //

function getColorStyle({ theme, color, type }: ColorStyleParams): ColorStyles {
  const colors = getColors(theme, color);
  const { lighter, light, main, contrastText } = colors;

  switch (type) {
    case "filled":
      return {
        color: contrastText,
        background: main,
      };
    case "outlined":
      return {
        color: main,
        border: "1px solid",
        borderColor: main,
        background: "transparent",
      };
    case "combined":
      return {
        color: main,
        border: "1px solid",
        borderColor: light,
        background: lighter,
      };
    default:
      return {
        color: main,
        background: lighter,
      };
  }
}

// ==============================|| AVATAR - SIZE STYLE ||============================== //

function getSizeStyle(size: SizeType): SizeStyles {
  switch (size) {
    case "badge":
      return {
        border: "2px solid",
        fontSize: "0.675rem",
        width: 20,
        height: 20,
      };
    case "xs":
      return {
        fontSize: "0.75rem",
        width: 24,
        height: 24,
      };
    case "sm":
      return {
        fontSize: "0.875rem",
        width: 32,
        height: 32,
      };
    case "lg":
      return {
        fontSize: "1.2rem",
        width: 52,
        height: 52,
      };
    case "xl":
      return {
        fontSize: "1.5rem",
        width: 64,
        height: 64,
      };
    case "md":
    default:
      return {
        fontSize: "1rem",
        width: 40,
        height: 40,
      };
  }
}

// ==============================|| AVATAR - STYLED ||============================== //

const AvatarStyle = styled(MuiAvatar, {
  shouldForwardProp: (prop: string) =>
    prop !== "color" && prop !== "type" && prop !== "size",
})<AvatarStyleProps>(({ theme, size, color, type }) => ({
  ...getSizeStyle(size),
  ...getColorStyle({ theme, color, type }),
  variants: [
    {
      props: { size: "badge" },
      style: {
        borderColor: theme?.vars?.palette.background.default,
      },
    },
  ],
}));

// ==============================|| AVATAR - COMPONENT ||============================== //

export default function Avatar({
  children,
  color = "primary",
  type,
  size = "md",
  ...others
}: AvatarProps): React.ReactElement {
  return (
    <AvatarStyle color={color} type={type} size={size} {...others}>
      {children}
    </AvatarStyle>
  );
}
